// Design A ("Corporate Band") Service Expense voucher -- Print Preview
// only, used when institution_profile.print_template is 'design_a'. Mirrors
// backend/src/pdfTemplates/designA/serviceExpenseAdapter.js's config
// mapping. ServiceExpenseVoucherBody.jsx (legacy) is untouched.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, numberFormatTax, formatToCompanyTimezone } from "../../../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../../../lib/designAWords";
import { fitItemName } from "../../../../../lib/designAItemFit";
import { formatPercent, resolveItemDiscountAmount, formatItemDiscountCell } from "../../../../../lib/designANumberFormat";
import { getServiceExpenseVoucherDetailsApi } from "../../../../../apiservice/service";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import { getPrimaryBankAccountApi, getBankAccountByIdsApi } from "../../../../../apiservice/accounts";
import { bankAccountToConfig } from "../../../../../lib/designABankAccount";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../../../components/common/VoucherBodyDesignA";

// Fixed-layout column widths (px); "Item Name" always takes whatever's left
// of the 698px content area. Same reasoning/numbers as
// serviceExpenseAdapter.js -- this voucher type's legacy item cell has no
// description/batch sub-line (plain item name only), so unlike Service/
// Sales Invoice there's no row-height or row-budget change here, only the
// column widths.
const PAGE_CONTENT_WIDTH = 698;
// disc bumped from 64 to 72 -- the header is mode-neutral ("Disc") now so
// needs less room than "DISC %" did, but a flat-amount discount value
// (e.g. "1,234.50") needs more room than a 1-2 digit percentage ever did.
// qty shrunk from 70 to 44 now that its header is "Qty" not "Quantity" --
// "Quantity" (8 chars) needed more room than 70px gave it and clipped to
// "QUANTI…" on a real print; "Qty" needs far less, freeing real width for
// the item name instead.
const COL_WIDTH = { hash: 36, rate: 100, per: 50, qty: 44, disc: 72, tax: 54, amount: 104 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemNameCell({ name, maxChars }) {
  const fit = fitItemName(name, { maxChars });
  return <span style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>;
}

export default function ServiceExpenseVoucherBodyDesignA({ service_ex_id, showStamp = false, showSignature = false, showShipping = false }) {
  const [data, setData] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [primaryBankAccount, setPrimaryBankAccount] = useState(null);
  const [paymentBankAccount, setPaymentBankAccount] = useState(null);

  const { institutionData = {} } = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!service_ex_id) return;
    getServiceExpenseVoucherDetailsApi({ service_ex_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setData(res.data[0]);
    });
  }, [service_ex_id]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
    getPrimaryBankAccountApi().then((res) => setPrimaryBankAccount(res?.data?.data || null)).catch(() => {});
  }, []);

  // A Service Expense pays money OUT, so its own voucher_transactions rows
  // use from_acc_id (not to_acc_id like Sales, where money comes IN) --
  // prefer whichever bank account this voucher's own payment actually used
  // over the branch-wide "primary" one (see getBankAccountByIds's comment
  // in backend/src/controllers/accounts.js).
  useEffect(() => {
    if (!data) return;
    const accIds = (data.trans || []).map((t) => t.from_acc_id || t.to_acc_id).filter(Boolean);
    if (!accIds.length) return;
    getBankAccountByIdsApi(accIds).then((res) => setPaymentBankAccount(res?.data?.data || null)).catch(() => {});
  }, [data]);

  if (!data) return null;

  const currency = institutionData?.currency || authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";

  const isIndividual = data.is_cal_type === "individual";
  const items = (data.details || []).map((item) => ({
    ...item,
    qty_display: `${formatPercent(item.item_qty)} ${item.unit_symbol || ""}` + (item.conversion > 1 ? `, ${formatPercent(item.retail_qty)} ${item.base_unit_name}` : ""),
  }));

  const grandTotal = isIndividual
    ? items.reduce((sum, d) => {
        const qty = d.item_qty || 0;
        const rate = Number(d.item_rate || 0);
        let total = qty * rate;
        total -= resolveItemDiscountAmount(d, total);
        total += (Number(d.item_tax_per || 0) / 100) * total;
        return sum + total;
      }, 0)
    : data.total_amount || 0;

  const useBranch = !!authInfo?.branch_info;
  const logoUrl = useBranch ? institution?.branch_logo : institution?.logo;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  const usedWidth =
    COL_WIDTH.hash + COL_WIDTH.rate + COL_WIDTH.per + COL_WIDTH.qty +
    (isIndividual ? COL_WIDTH.disc : 0) + (isIndividual && !isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.amount;
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [
    { label: "#", width: `${COL_WIDTH.hash}px` },
    { label: "Item Name" },
    { label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` },
    { label: "Per", align: "right", width: `${COL_WIDTH.per}px` },
    { label: "Qty", align: "right", width: `${COL_WIDTH.qty}px` },
  ];
  if (isIndividual) {
    // Not "Disc %" -- a row's discount can be entered as either a % or a
    // flat amount (discount_mode), so the header stays mode-neutral and
    // each row's own value (formatItemDiscountCell) says which one it is.
    // "Disct" matches the legacy entry screen's own abbreviation for this.
    columns.push({ label: "Disct", align: "right", width: `${COL_WIDTH.disc}px` });
    if (!isGSTDisabled) columns.push({ label: `${taxLabel} %`, align: "right", width: `${COL_WIDTH.tax}px` });
  }
  columns.push({ label: isIndividual ? "Total" : "Amount", align: "right", width: `${COL_WIDTH.amount}px` });

  const rows = items.map((d, i) => {
    const cells = [
      String(i + 1),
      <ItemNameCell key="n" name={d.item_name} maxChars={itemMaxChars} />,
      <span className="da-num-cell" key="r">{numberFormat(d.item_rate)}</span>,
      <span className="da-num-cell" key="p">{isIndividual ? d.unit_name : d.per_unit_symbol}</span>,
      <span className="da-num-cell" key="q">{isIndividual ? formatPercent(d.item_qty) : d.qty_display}</span>,
    ];

    let rawAmount;
    if (isIndividual) {
      cells.push(<span className="da-num-cell" key="d">{formatItemDiscountCell(d, numberFormat)}</span>);
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{formatPercent(d.item_tax_per)}</span>);
      let total = Number(d.item_qty || 0) * Number(d.item_rate || 0);
      total -= resolveItemDiscountAmount(d, total);
      total += (Number(d.item_tax_per || 0) / 100) * total;
      rawAmount = total;
    } else {
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: false, rawAmount };
  });

  const totalsRows = [];
  totalsRows.push({ label: "Sub Total", value: numberFormat(data.sub_total || 0) });
  totalsRows.push({ label: "Discount Amount", value: `- ${numberFormat(data.total_discount || 0)}`, credit: true });

  let taxSummary = null;
  if (isIndiaGst) {
    if (data.place_of_supply_type === "intra") {
      totalsRows.push({ label: "CGST", value: numberFormatTax(data.cgst_amount || 0) });
      totalsRows.push({ label: "SGST", value: numberFormatTax(data.sgst_amount || 0) });
      taxSummary = { mode: "cgst_sgst", rows: [{ rate: "", taxable: numberFormat(data.sub_total || 0), cgst: numberFormatTax(data.cgst_amount || 0), sgst: numberFormatTax(data.sgst_amount || 0) }] };
    }
    if (data.place_of_supply_type === "inter") {
      totalsRows.push({ label: "IGST", value: numberFormatTax(data.igst_amount || 0) });
      taxSummary = { mode: "igst", rows: [{ rate: "", taxable: numberFormat(data.sub_total || 0), igst: numberFormatTax(data.igst_amount || 0) }] };
    }
    if (data.gst_calculated === "no") {
      // Same literal "IGST" fallback label the legacy component uses -- see
      // serviceExpenseAdapter.js's note on this being real, intentional
      // behavior rather than a label bug.
      totalsRows.push({ label: "IGST", value: numberFormatTax(data.igst_amount || 0) });
    }
  } else if (!isIndia) {
    totalsRows.push({ label: taxLabel, value: numberFormat(data.total_tax || 0) });
  }

  const quantityTotal = items.reduce((sum, d) => sum + (Number(d.item_qty) || 0), 0);

  const bankDetails = bankAccountToConfig(paymentBankAccount || primaryBankAccount);

  const config = {
    docTitle: "SERVICE EXPENSE",
    docNumber: data.service_ex_voucher_no,
    institution: { logoUrl, companyName, companyGstin: isIndia ? institutionData?.vat_license : null },
    party: {
      billToLabel: "Supplier",
      billTo: {
        name: data.acc_name,
        lines: [
          data.acc_code ? `Supplier Code: ${data.acc_code}` : null,
          data.address ? `Address: ${data.address}` : null,
          data.contact_no ? `Contact No: +${data.contact_no}` : null,
          data.licence_no ? `License No: ${data.licence_no}` : null,
        ].filter(Boolean),
      },
      shipTo:
        showShipping && data.shipping_address
          ? {
              lines: [
                data.shipping_contact_person ? `Contact Person: +${data.shipping_contact_person}` : null,
                `Address: ${data.shipping_address}`,
                data.shipping_po_box ? `Po Box: ${data.shipping_po_box}` : null,
                data.shipping_contact_no ? `Contact No: +${data.shipping_contact_no}` : null,
              ].filter(Boolean),
            }
          : null,
      fromLabel: "From",
      from: {
        name: companyName,
        lines: [
          institutionData?.street_address || null,
          institutionData?.phone_no ? `+${institutionData.phone_no}` : null,
        ].filter(Boolean),
      },
      metaLabel: "Voucher Details",
      meta: [
        { label: "Expense Date", value: (formatToCompanyTimezone(data.created_date) || "").split(" ")[0] },
        { label: "Expense By", value: (data.full_name || "").split(" ")[0] },
        { label: "Qty Total", value: String(quantityTotal) },
        ...(institutionData?.vat_license ? [{ label: isUAE ? "TRN No" : "Tax Reg No", value: institutionData.vat_license }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty: quantityTotal,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(grandTotal),
    paidValue: numberFormat(data.paid_total || 0),
    dueValue: numberFormat(data.due_total || 0),
    amountInWordsText: convertAmountToWordsDesignA(grandTotal, { country, currency }),
    bankDetails,
    taxSummary,
    terms: institutionData?.invoice_default_terms || data.narration || "Thanks for your business.",
    signatory: {
      companyName,
      stampUrl: showStamp ? institutionData?.stamp : null,
      signatureUrl: showSignature ? institutionData?.signature : null,
    },
    bonusNote: null,
  };

  return <VoucherBodyDesignA config={config} />;
}
